import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Phone, PhoneCall, PhoneIncoming, CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUTO_DIAL_NUMBER = '+17045895687';

const VoiceExperiencePage = () => {
  const { token } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callType, setCallType] = useState('outgoing');
  const [callStatus, setCallStatus] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [callId, setCallId] = useState(null);
  const [loading, setLoading] = useState(false);
  const hasAutoDialed = useRef(false);

  const simulateCall = useCallback(async (number) => {
    const dialNumber = number || phoneNumber;
    if (!dialNumber || dialNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      let newCallId = `local-${Date.now()}`;

      // Log call to backend if authenticated
      if (token) {
        try {
          const response = await axios.post(
            `${API}/voice-calls`,
            { phone_number: dialNumber, call_type: callType },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          newCallId = response.data.id;
        } catch (apiError) {
          console.warn('Could not log call to backend:', apiError);
        }
      }

      setCallId(newCallId);
      setCallStatus('initiated');
      toast.success('Call initiated');

      // Simulate call progression
      setTimeout(() => {
        setCallStatus('ringing');
        updateCallStatus(newCallId, 'ringing');
      }, 1500);

      setTimeout(() => {
        setCallStatus('connected');
        updateCallStatus(newCallId, 'connected');
        simulateConversation(newCallId);
      }, 4000);
    } catch (error) {
      console.error('Failed to initiate call:', error);
      toast.error('Failed to initiate call');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, callType]);

  // Auto-type phone number and auto-click start on page load
  useEffect(() => {
    if (hasAutoDialed.current) return;
    hasAutoDialed.current = true;

    const chars = AUTO_DIAL_NUMBER.split('');
    let current = '';

    const TYPING_DELAY = 5000; // 5 second delay before typing starts

    chars.forEach((char, i) => {
      setTimeout(() => {
        current += char;
        setPhoneNumber(current);

        // After last character, auto-click the start button
        if (i === chars.length - 1) {
          setTimeout(() => {
            simulateCall(AUTO_DIAL_NUMBER);
          }, 500);
        }
      }, TYPING_DELAY + (i + 1) * 90);
    });
  }, [simulateCall]);

  const simulateConversation = async (cId) => {
    const conversation = [
      { speaker: 'AI', text: 'Hello! This is Digian AI Support. How can I assist you today?', delay: 2000 },
      { speaker: 'User', text: 'Hi, I need help resetting my password.', delay: 3000 },
      { speaker: 'AI', text: 'I can help you with that. Let me verify your account information.', delay: 3000 },
      { speaker: 'AI', text: 'I\'ve sent a password reset link to your registered email. Please check your inbox.', delay: 4000 },
      { speaker: 'User', text: 'Great, I received it. Thank you!', delay: 2000 },
      { speaker: 'AI', text: 'You\'re welcome! Is there anything else I can help you with today?', delay: 2000 },
      { speaker: 'User', text: 'No, that\'s all. Thanks!', delay: 2000 },
      { speaker: 'AI', text: 'Perfect! Have a great day. Goodbye!', delay: 2000 }
    ];

    let currentTranscript = [];
    for (const message of conversation) {
      await new Promise(resolve => setTimeout(resolve, message.delay));
      currentTranscript.push({ speaker: message.speaker, text: message.text, timestamp: new Date().toISOString() });
      setTranscript([...currentTranscript]);
    }

    // End call
    setTimeout(() => {
      setCallStatus('completed');
      updateCallStatus(cId, 'completed', currentTranscript);
      toast.success('Call completed');
    }, 1000);
  };

  const updateCallStatus = async (cId, status, transcriptData = null) => {
    try {
      await axios.put(
        `${API}/voice-calls/${cId}/status?status=${status}`,
        transcriptData ? { transcript: transcriptData } : null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to update call status:', error);
    }
  };

  const resetCall = () => {
    setCallStatus(null);
    setTranscript([]);
    setCallId(null);
    setPhoneNumber('');
    hasAutoDialed.current = false;
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-slate-900 mb-4" data-testid="voice-experience-heading">
            AI Voice IT Support Experience
          </h1>
          <p className="text-lg text-slate-600">
            Experience our AI-powered voice support in action (Simulated Demo)
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Call Control Panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-8 shadow-lg"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Call Setup</h2>

            {!callStatus ? (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="847-915-0956"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    data-testid="voice-phone-input"
                    className="h-12"
                  />
                  <p className="text-sm text-slate-500">Enter your phone number to receive a simulated AI call</p>
                </div>

                <div className="space-y-3">
                  <Label>Call Type</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setCallType('outgoing')}
                      data-testid="call-type-outgoing"
                      className={`p-4 rounded-lg border-2 transition-all ${
                        callType === 'outgoing'
                          ? 'border-indigo-600 bg-indigo-50'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <PhoneCall className="w-6 h-6 mx-auto mb-2 text-indigo-600" />
                      <div className="font-medium text-sm">Outgoing</div>
                      <div className="text-xs text-slate-600">Call AI Support</div>
                    </button>
                    <button
                      onClick={() => setCallType('incoming')}
                      data-testid="call-type-incoming"
                      className={`p-4 rounded-lg border-2 transition-all ${
                        callType === 'incoming'
                          ? 'border-indigo-600 bg-indigo-50'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <PhoneIncoming className="w-6 h-6 mx-auto mb-2 text-indigo-600" />
                      <div className="font-medium text-sm">Incoming</div>
                      <div className="text-xs text-slate-600">Receive AI Call</div>
                    </button>
                  </div>
                </div>

                <Button
                  onClick={() => simulateCall()}
                  disabled={loading}
                  className="w-full h-12"
                  data-testid="start-call-btn"
                >
                  {loading ? 'Initiating...' : `Start ${callType === 'outgoing' ? 'Outgoing' : 'Incoming'} Call`}
                </Button>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900">
                      <strong>Demo Mode:</strong> This is a simulated experience. No actual phone call will be made.
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Call Status */}
                <div className="text-center py-8">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-10 h-10 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-slate-900 mb-2" data-testid="call-status">
                    {callStatus === 'initiated' && 'Initiating Call...'}
                    {callStatus === 'ringing' && 'Ringing...'}
                    {callStatus === 'connected' && 'Connected'}
                    {callStatus === 'completed' && 'Call Ended'}
                  </div>
                  <div className="text-slate-600">{phoneNumber}</div>
                </div>

                {callStatus === 'completed' && (
                  <Button onClick={resetCall} variant="outline" className="w-full" data-testid="reset-call-btn">
                    Start New Call
                  </Button>
                )}
              </div>
            )}
          </motion.div>

          {/* Live Transcript */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-lg glass overflow-hidden"
          >
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-6">
              <h2 className="text-2xl font-bold text-white">Live Transcript</h2>
              <p className="text-indigo-100 text-sm">Real-time conversation</p>
            </div>

            <div className="p-6 h-96 overflow-y-auto" data-testid="transcript-container">
              {transcript.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <Phone className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Transcript will appear here once the call starts</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {transcript.map((message, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${message.speaker === 'User' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`transcript-message-${index}`}
                    >
                      <div
                        className={`max-w-xs px-4 py-3 rounded-lg ${
                          message.speaker === 'User'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-100 text-slate-900'
                        }`}
                      >
                        <div className="text-xs font-medium mb-1 opacity-75">{message.speaker}</div>
                        <div className="text-sm">{message.text}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 bg-white rounded-2xl border border-slate-200 p-8"
        >
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Key Features</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Natural Conversations', desc: 'Human-like AI interactions with context awareness' },
              { title: 'Real-time Transcription', desc: 'Live conversation transcription and analysis' },
              { title: 'Smart Routing', desc: 'Intelligent escalation to human agents when needed' }
            ].map((feature, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">{feature.title}</h4>
                  <p className="text-sm text-slate-600">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default VoiceExperiencePage;
